import { Button as ButtonPrimitive } from "@base-ui/react/button"
import { cva, type VariantProps } from "class-variance-authority"
import { Loader2 } from "lucide-react"

import { cn } from "@/lib/utils"

/**
 * JustGold Button — matches Figma design (Just Gold Website › node 38:3)
 *
 * Variants:  brand | outline-brand | ghost | secondary | destructive | link
 * Sizes:     sm | md (default) | lg
 * States:    default · hover · active · focus · disabled · loading
 *
 * Design rules (from Figma + standard.md):
 *  - NO drop shadow on any variant
 *  - Hover = background color shift only (no translate / lift)
 *  - Active = slightly darker bg + 1px press-down
 *  - Focus = visible ring (shadow-focus token), no border change
 *  - Disabled = muted bg, no pointer-events, opacity preserved
 *  - Font = Host Grotesk Medium (500) for md/lg, same for sm
 *  - Border radius = iOS squircle approx (16px) — matches 100% corner smoothing in Figma
 */
const buttonVariants = cva(
  // ── Base ──────────────────────────────────────────────────────────────────
  [
    "inline-flex items-center justify-center gap-2 whitespace-nowrap",
    "font-medium select-none cursor-pointer",
    "rounded-[10px] border border-transparent",
    "transition-colors duration-200",
    "outline-none",
    // Focus ring — uses shadow-focus token
    "focus-visible:ring-[3px] focus-visible:ring-[rgba(14,58,138,0.25)]",
    // Disabled
    "disabled:pointer-events-none disabled:opacity-60",
    // SVG defaults
    "[&_svg]:pointer-events-none [&_svg]:shrink-0",
    // Reduced motion
    "motion-reduce:transition-none",
  ],
  {
    variants: {
      variant: {
        /**
         * brand — primary CTA. Matches Figma exactly.
         * bg: primary-600 (#0E3A8A) | text: white
         * hover: primary-500 (lighter) | active: primary-700 (darker)
         */
        brand: [
          "bg-primary-600 text-white border-primary-600",
          "hover:bg-primary-700 hover:border-primary-700",
          "active:bg-primary-800 active:border-primary-800",
          "disabled:bg-primary-300 disabled:border-primary-300 disabled:text-white",
        ],

        /**
         * accent — gold CTA for dark brand surfaces.
         */
        accent: [
          "bg-accent-500 text-primary-900 border-accent-500",
          "hover:bg-accent-400 hover:border-accent-400",
          "active:bg-accent-600 active:border-accent-600",
          "disabled:bg-accent-200 disabled:border-accent-200 disabled:text-primary-700",
        ],

        /**
         * outline-brand — bordered version on white bg.
         */
        "outline-brand": [
          "bg-transparent text-primary-600 border-primary-600",
          "hover:bg-primary-50",
          "active:bg-primary-100",
          "disabled:border-primary-300 disabled:text-primary-300",
        ],

        /**
         * secondary — neutral surface button.
         */
        secondary: [
          "bg-neutral-100 text-neutral-800 border-neutral-200",
          "hover:bg-neutral-200 hover:border-neutral-300",
          "active:bg-neutral-300",
          "disabled:bg-neutral-100 disabled:text-neutral-400",
        ],

        /**
         * ghost — transparent, subtle hover.
         */
        ghost: [
          "bg-transparent text-neutral-700 border-transparent",
          "hover:bg-neutral-100 hover:text-neutral-900",
          "active:bg-neutral-200",
          "disabled:text-neutral-400",
        ],

        /**
         * destructive — danger actions.
         */
        destructive: [
          "bg-danger text-white border-danger",
          "hover:bg-[#B91C1C] hover:border-[#B91C1C]",
          "active:bg-[#991B1B]",
          "disabled:bg-danger/40 disabled:border-danger/40",
        ],

        /**
         * link — inline text link style.
         */
        link: [
          "bg-transparent text-primary-600 border-transparent underline-offset-4",
          "hover:underline hover:text-primary-500",
          "active:text-primary-700",
          "disabled:text-primary-300",
        ],
      },

      size: {
        sm: "h-9 px-4 text-sm [&_svg:not([class*='size-'])]:size-4",
        md: "h-11 px-5 py-3 text-base [&_svg:not([class*='size-'])]:size-[18px]",
        lg: "h-13 px-7 py-3.5 text-lg [&_svg:not([class*='size-'])]:size-5",
      },

      loading: {
        true: "relative pointer-events-none",
        false: "",
      },
    },

    defaultVariants: {
      variant: "brand",
      size: "md",
      loading: false,
    },
  }
)

type ButtonProps = ButtonPrimitive.Props &
  VariantProps<typeof buttonVariants> & {
    loading?: boolean
    loadingText?: string
  }

function Button({
  className,
  variant = "brand",
  size = "md",
  loading = false,
  loadingText,
  children,
  disabled,
  ...props
}: ButtonProps) {
  return (
    <ButtonPrimitive
      data-slot="button"
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(buttonVariants({ variant, size, loading, className }))}
      {...props}
    >
      {loading && (
        <Loader2
          className="animate-spin shrink-0"
          aria-hidden="true"
          size={size === "sm" ? 14 : size === "lg" ? 20 : 16}
        />
      )}
      {loading && loadingText ? loadingText : children}
    </ButtonPrimitive>
  )
}

export { Button, buttonVariants }
export type { ButtonProps }
